import { supabase } from '../../supabaseClient';
import { ENTRY_TYPE, INSTALLMENT_STATUS } from '../constants/financial';
import { formatLocalISO, todayISO } from './financialPeriod';
import { fetchAllRows } from './supabaseFetchAll';

// Divide um valor (em reais) em `count` parcelas iguais em centavos, com a
// ÚLTIMA parcela absorvendo o resto do arredondamento. Garante que a soma das
// parcelas seja exatamente igual a `totalValue` (sem vazamento de centavos).
export const splitInteger = (totalValue, count) => {
  const n = Math.max(1, count);
  const totalCents = Math.round((Number(totalValue) || 0) * 100);
  const baseCents = Math.floor(totalCents / n);
  const result = [];
  let accumulated = 0;
  for (let i = 0; i < n; i++) {
    const cents = i === n - 1 ? totalCents - accumulated : baseCents;
    accumulated += cents;
    result.push(cents / 100);
  }
  return result;
};

// Gera o cronograma de parcelas: `count` parcelas com valor rateado (última
// absorve o resto) e vencimentos mensais a partir de `firstDueISO` (parcela 1
// vence na data informada; as demais somam 1 mês cada). Retorna valores
// numéricos (não formatados).
export const prorateInstallments = (totalValue, count, firstDueISO) => {
  const n = Math.max(1, Math.min(48, count));
  const values = splitInteger(totalValue, n);
  const baseISO = firstDueISO || todayISO();
  const [y, m, d] = baseISO.split('-').map(Number);
  const firstDueDate = new Date(y, m - 1, d);
  firstDueDate.setHours(0, 0, 0, 0);
  return values.map((value, i) => {
    const dueDate = new Date(firstDueDate);
    if (i > 0) dueDate.setMonth(dueDate.getMonth() + i);
    return {
      installment_number: i + 1,
      due_date: formatLocalISO(dueDate),
      value,
    };
  });
};

// Status derivado p/ exibição. ATRASADO nunca é persistido — calculado
// a partir de due_date vs data atual. PAGO sempre prevalece (RN06/CA07).
export const getInstallmentStatus = (inst, today = todayISO()) => {
  if (inst?.status === INSTALLMENT_STATUS.PAGO) return INSTALLMENT_STATUS.PAGO;
  if (inst?.due_date && inst.due_date < today) return INSTALLMENT_STATUS.ATRASADO;
  return INSTALLMENT_STATUS.PENDENTE;
};

export const saveInstallment = async (id = undefined, item, userId = undefined) => {
  if (id) {
    // RN05: rastreabilidade de edição (quem/quando) no update de parcela.
    return await supabase
      .from('Installment')
      .update({ ...item, updated_at: new Date(), updated_by: userId ?? null })
      .match({ id });
  }
  return await supabase
    .from('Installment')
    .insert([{ ...item }]);
};

// Atualiza uma parcela JÁ PAGA (valor, vencimento, data de pagamento, banco,
// forma). Mantém status PAGO. Grava auditoria (RN05). O saldo do banco é
// calculado (inicial + pagas), então não há reconciliação a fazer aqui
// (Ajuste 06 — saldo inicial imutável).
export const updatePaidInstallment = async (id, patch, userId) => {
  const { data: cur, error: fetchErr } = await supabase
    .from('Installment')
    .select('status')
    .eq('id', id)
    .single();
  if (fetchErr || !cur) return { error: fetchErr || new Error('Parcela não encontrada') };
  if (cur.status !== INSTALLMENT_STATUS.PAGO) {
    return { error: new Error('Parcela não está paga') };
  }

  const update = {
    due_date: patch.due_date,
    payment_method: patch.payment_method ?? null,
    status: INSTALLMENT_STATUS.PAGO,
    updated_at: new Date(),
    updated_by: userId ?? null,
  };
  if (patch.value != null) update.value = Number(patch.value);
  if (patch.bank_id !== undefined) update.bank_id = patch.bank_id;
  if (patch.payment_date) update.payment_date = patch.payment_date;

  return await supabase.from('Installment').update(update).match({ id });
};

// Reverte uma parcela PAGA de volta para PENDENTE (RN06/RN07): limpa os dados de
// pagamento (data, forma, banco). Grava auditoria (RN05). A parcela volta a
// participar normalmente do fluxo (contas a pagar/receber, projeções) por
// depender só do status. O saldo do banco é calculado (inicial + pagas), então
// remover o status PAGO já reflete no saldo automaticamente.
export const revertInstallmentToPending = async (id, userId) => {
  const { data: inst, error: fetchErr } = await supabase
    .from('Installment')
    .select('status')
    .eq('id', id)
    .single();
  if (fetchErr || !inst) {
    return { error: fetchErr || new Error('Parcela não encontrada') };
  }
  if (inst.status !== INSTALLMENT_STATUS.PAGO) {
    return { error: new Error('Parcela não está paga') };
  }

  return await supabase
    .from('Installment')
    .update({
      status: INSTALLMENT_STATUS.PENDENTE,
      payment_date: null,
      payment_method: null,
      bank_id: null,
      updated_at: new Date(),
      updated_by: userId ?? null,
    })
    .match({ id });
};

export const saveInstallments = async (items) => {
  // .select() retorna as linhas inseridas com id — usado pelo
  // FinancialEntryModal para depois marcar como PAGO as que o usuário
  // escolheu (via markInstallmentAsPaid, mantém Bank.balance consistente).
  return await supabase
    .from('Installment')
    .insert(items)
    .select();
};

export const loadInstallmentsByEntry = async (financialEntryId) => {
  let { data, error } = await supabase
    .from('Installment')
    .select('*, Bank(*)')
    .eq('financial_entry_id', financialEntryId)
    .is('deleted_at', null)
    .order('installment_number', { ascending: true });
  if (!error) {
    return data;
  }
  return [];
};

// Carrega parcelas (não-deletadas) de vários lançamentos de uma vez. Usado na
// idempotência plural da cobrança da OS (verifica se ALGUMA parcela de QUALQUER
// lançamento da OS já foi paga).
export const loadInstallmentsByEntries = async (financialEntryIds) => {
  if (!financialEntryIds || financialEntryIds.length === 0) return [];
  let { data, error } = await supabase
    .from('Installment')
    .select('*, Bank(*)')
    .in('financial_entry_id', financialEntryIds)
    .is('deleted_at', null)
    .order('installment_number', { ascending: true });
  if (!error) {
    return data;
  }
  return [];
};

export const loadInstallmentsByCompany = async (companyId, filters = {}) => {
  const buildQuery = () => {
    let query = supabase
      .from('Installment')
      .select('*, financial_entry:FinancialEntry!inner(*, AccountPlan(*), CostCenter(*)), Bank(*)')
      .eq('financial_entry.company_id', companyId)
      .is('deleted_at', null)
      .is('financial_entry.deleted_at', null);

    if (filters.type) {
      query = query.eq('financial_entry.type', filters.type);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.dueDateFrom) {
      query = query.gte('due_date', filters.dueDateFrom);
    }
    if (filters.dueDateTo) {
      query = query.lte('due_date', filters.dueDateTo);
    }

    return query
      .order('due_date', { ascending: true })
      .order('id', { ascending: true });
  };

  const { data } = await fetchAllRows(buildQuery);
  return data || [];
};

export const markInstallmentAsPaid = async (id, paymentData) => {
  // Lê o estado atual da parcela (idempotência)
  const { data: inst, error: fetchErr } = await supabase
    .from('Installment')
    .select('status')
    .eq('id', id)
    .single();
  if (fetchErr || !inst) return { error: fetchErr || new Error('Parcela não encontrada') };
  if (inst.status === INSTALLMENT_STATUS.PAGO) {
    return { error: new Error('Parcela já está paga') };
  }

  // O saldo do banco é calculado (inicial + parcelas pagas). Marcar como PAGO
  // com bank_id já reflete no saldo — sem mutar Bank.balance (Ajuste 06).
  return await supabase
    .from('Installment')
    .update({
      status: INSTALLMENT_STATUS.PAGO,
      payment_date: paymentData.payment_date,
      payment_method: paymentData.payment_method,
      bank_id: paymentData.bank_id,
      receipt_url: paymentData.receipt_url || null,
    })
    .match({ id });
};

export const loadStatementByCompany = async (companyId, filters = {}) => {
  const buildQuery = () => {
    let query = supabase
      .from('Installment')
      .select('*, financial_entry:FinancialEntry!inner(*, AccountPlan(*), CostCenter(*)), Bank(*)')
      .eq('financial_entry.company_id', companyId)
      .eq('status', 'pago')
      .is('deleted_at', null)
      .is('financial_entry.deleted_at', null);

    if (filters.bankId) {
      query = query.eq('bank_id', filters.bankId);
    }
    if (filters.paymentDateFrom) {
      query = query.gte('payment_date', filters.paymentDateFrom);
    }
    if (filters.paymentDateTo) {
      query = query.lte('payment_date', filters.paymentDateTo);
    }

    return query
      .order('payment_date', { ascending: false })
      .order('id', { ascending: true });
  };

  const { data } = await fetchAllRows(buildQuery);
  return data || [];
};

// Soma líquida (receita +, despesa -) das parcelas PAGAS com payment_date >= fromDate,
// restrita a parcelas com banco. Usada no extrato para obter o saldo de abertura
// do período: saldoInicialDoPeríodo = saldoAtual - este total.
// Sem fromDate, considera todas as pagas (período "Todos" → saldo de abertura).
export const loadNetPaidSince = async (companyId, fromDate, bankId) => {
  const buildQuery = () => {
    let query = supabase
      .from('Installment')
      .select('value, financial_entry:FinancialEntry!inner(type, company_id, deleted_at)')
      .eq('financial_entry.company_id', companyId)
      .eq('status', INSTALLMENT_STATUS.PAGO)
      .not('bank_id', 'is', null)
      .is('deleted_at', null)
      .is('financial_entry.deleted_at', null);

    if (fromDate) {
      query = query.gte('payment_date', fromDate);
    }
    if (bankId) {
      query = query.eq('bank_id', bankId);
    }

    return query.order('id', { ascending: true });
  };

  const { data, error } = await fetchAllRows(buildQuery);
  if (error || !data) return 0;

  return data.reduce((sum, i) => {
    const value = Number(i.value) || 0;
    const direction = i.financial_entry?.type === ENTRY_TYPE.RECEITA ? 1 : -1;
    return sum + value * direction;
  }, 0);
};

// Soma líquida das parcelas PAGAS agrupada por banco (receita +, despesa -).
// Base para o SALDO ATUAL de cada banco: saldoAtual = saldo_inicial + net[bank]
// (Ajuste 06 — Bank.balance é o saldo inicial imutável). Retorna { [bankId]: net }.
export const loadNetPaidByBank = async (companyId) => {
  const { data, error } = await fetchAllRows(() =>
    supabase
      .from('Installment')
      .select('value, bank_id, financial_entry:FinancialEntry!inner(type, company_id, deleted_at)')
      .eq('financial_entry.company_id', companyId)
      .eq('status', INSTALLMENT_STATUS.PAGO)
      .not('bank_id', 'is', null)
      .is('deleted_at', null)
      .is('financial_entry.deleted_at', null)
      .order('id', { ascending: true })
  );
  if (error || !data) return {};

  const map = {};
  data.forEach((i) => {
    const direction = i.financial_entry?.type === ENTRY_TYPE.RECEITA ? 1 : -1;
    map[i.bank_id] = (map[i.bank_id] || 0) + (Number(i.value) || 0) * direction;
  });
  return map;
};

// Hard-delete das parcelas pendentes de um lançamento. Usado pelo modo
// edição do FinancialEntryModal quando o usuário regrava as parcelas
// (apenas permitido quando não há nenhuma parcela PAGA). Filtra
// defensivamente por status != PAGO pra nunca remover parcela que já
// movimentou Bank.balance. Hard-delete porque parcelas pendentes
// regravadas não têm relação posterior nem precisam de auditoria —
// decisão arquitetural do time, fora do padrão soft-delete do projeto.
export const deletePendingInstallmentsByEntry = async (financialEntryId) => {
  return await supabase
    .from('Installment')
    .delete()
    .eq('financial_entry_id', financialEntryId)
    .neq('status', INSTALLMENT_STATUS.PAGO);
};

export const deleteInstallment = async (id, userId) => {
  // O saldo do banco é calculado (inicial + parcelas pagas não-deletadas), então
  // soft-deletar uma parcela paga já a remove do cálculo automaticamente.
  return await supabase
    .from('Installment')
    .update({ deleted_at: new Date(), deleted_by: userId })
    .match({ id });
};
